import React from 'react'
import { useField } from 'formik'
import Select from '../../Select/Select'
import { modeTypes } from '../constants'
import { Choice, getChoiceLabel, withDefaultsFirst } from './selectFieldHelpers'

interface SelectFieldProps {
  field: any
  mode?: string
  /** True when rendered inside a group's contextual popover — the menu must be
   *  portaled to <body> to escape the popover's overflow clipping. */
  inGroupPopover?: boolean
}

interface Option {
  value: string
  label: string
}

/**
 * Dropdown SELECT (asset-form `display_type = SELECT`), rendered with the same
 * venus `Select` (react-select v2) the CMS entry editor's DropdownComponent uses,
 * so the look and behaviour match: chips + "Select All" checkbox menu for multi,
 * typeahead + clear for single. Choices are mapped to `{ value, label }` options;
 * the Formik value stays as the raw value(s) — a string (single) or string[] (multi).
 */
const SelectField: React.FC<SelectFieldProps> = ({ field, mode, inGroupPopover }) => {
  const { formFieldUid, title } = field
  const [inputField, , helpers] = useField(formFieldUid)
  const isMultiple = !!field.multiple
  const isReadOnly = mode === modeTypes.readOnly || field.readOnly
  const isKeyValue = !!field?.field_metadata?.is_key_value
  const choices: Choice[] = withDefaultsFirst(field?.field_metadata?.choices || [])

  const options: Option[] = choices.map((choice) => ({
    value: choice.value,
    label: getChoiceLabel(choice, isKeyValue)
  }))
  const optionOf = (value: string): Option | null =>
    options.find((option) => option.value === value) || null

  const rawValue = inputField.value
  const hasValue = isMultiple
    ? Array.isArray(rawValue) && rawValue.length > 0
    : rawValue != null && rawValue !== ''

  // venus Select is controlled by option objects; a stored value that matches no
  // live choice (stale) simply resolves to nothing selected without being mutated.
  const selectValue = isMultiple
    ? Array.isArray(rawValue)
      ? (rawValue.map(optionOf).filter(Boolean) as Option[])
      : []
    : hasValue
      ? optionOf(rawValue)
      : null

  const handleChange = (selected: any): void => {
    if (isMultiple) {
      helpers.setValue(Array.isArray(selected) ? selected.map((option: Option) => option.value) : [])
    } else {
      helpers.setValue(selected ? selected.value : null)
    }
  }

  return (
    <div
      className="asset-details-v2-fields-container__select-field"
      data-test-id={`cs-asset-details-field-${formFieldUid}-wrapper`}
    >
      <Select
        testId={`cs-asset-details-field-${formFieldUid}-select`}
        value={selectValue}
        onChange={handleChange}
        options={options}
        isMulti={isMultiple}
        isDisabled={isReadOnly}
        isSearchable
        isClearable={isMultiple ? false : hasValue}
        isSelectAll={isMultiple}
        placeholder={`Select ${title || ''}`.trim()}
        version="v2"
        width="100%"
        maxMenuHeight={250}
        menuPlacement="auto"
        {...(inGroupPopover && typeof document !== 'undefined'
          ? {
              // Escape the group popover's overflow clipping by portaling the menu.
              menuPortalTarget: document.body,
              styles: {
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                // The portaled menu lands in <body>, outside the field wrapper, so the scoped
                // 12px CSS can't reach it and venus's `.Portal__option` (16px) applies. Size the
                // options per-instance here (no global `.Portal__option` → no leak); !important
                // beats venus's non-important rule. Use the literal 0.75rem (not var(--size-font-small))
                // so it can't be dropped if that custom property isn't defined in the portal context.
                // Multi-select options render a venus <Checkbox>; its `.Checkbox__label` carries the
                // venus 16px default, and the option `fontSize` does not cascade into it. The portaled
                // menu also sits outside the field wrapper, so the scoped 12px CSS can't reach it —
                // size the nested label per-instance via a nested selector (no global `.Portal__` leak).
                // line-height matches the 16px box so the 12px label centers (the CSS centering rule
                // is scoped to the field wrapper the portaled menu escapes, so carry it here too).
                menuList: (base: any) => ({ ...base, fontSize: '0.75rem !important', '.Checkbox__label': { fontSize: '0.75rem !important', lineHeight: '1rem !important' } }),
                option: (base: any) => ({ ...base, fontSize: '0.75rem !important', '.Checkbox__label': { fontSize: '0.75rem !important', lineHeight: '1rem !important' } })
              }
            }
          : {})}
      />
    </div>
  )
}

export default SelectField
